import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrintRows } from 'hooks/usePrintRows';
import { ApplicationState } from 'store';
import { AiManagementCard, AiManagementState } from 'store/modules/ai_management_store/types';
import { getColumns } from 'store/modules/ai_management_store/actions';
import { deleteCard, listArchivedCards, restoreCards } from 'services/actions/ai_management_cards';
import { formatDate, formatDateTime } from 'services/ai_management_dates';
import LongText from 'components/atoms/long_text';
import Pagination from 'components/atoms/pagination';
import AiManagementCardMenu from 'components/molecules/ai_management_card_menu';
import AiManagementColumnPickerModal from 'components/molecules/ai_management_column_picker_modal';
import AiManagementCardModal from 'components/organisms/ai_management_card_modal';
import { Container } from './styles';

const PAGE_SIZE = 20;

const archivedByLabel = (card: AiManagementCard) => {
  const name = card.archived_by?.name;
  if (!name) return '—';
  if (!card.archived_at) return name;
  return `${name} em ${formatDate(card.archived_at)}`;
};

const AiManagementArchivedList: React.FC = () => {
  const dispatch = useDispatch();
  const { columns, filters } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );
  const [cards, setCards] = useState<AiManagementCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [openCard, setOpenCard] = useState<AiManagementCard | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<number[] | null>(null);
  const [restoring, setRestoring] = useState(false);
  const { printRef, printingSelection, print } = usePrintRows({
    documentTitle: 'Gestão IA - Licitações arquivadas',
    hasSelection: selected.size > 0,
  });

  const load = () => {
    setLoading(true);
    setError(false);
    listArchivedCards()
      .then((res) => {
        if (!res || res.status >= 400) {
          setError(true);
          return;
        }
        setCards(Array.isArray(res.data?.cards) ? res.data.cards : []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  useEffect(() => {
    dispatch(getColumns(filters));
  }, [dispatch, filters]);

  const targetColumns = useMemo(
    () =>
      [...columns]
        .filter((column) => column.permissions.accepts_cards)
        .sort((a, b) => a.sort_group - b.sort_group || a.position - b.position),
    [columns],
  );

  const totalPages = Math.max(1, Math.ceil(cards.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageCards = useMemo(
    () => cards.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [cards, currentPage],
  );
  const pageIds = pageCards.map((card) => card.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const visibleCards = printingSelection
    ? cards.filter((card) => selected.has(card.id))
    : pageCards;

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const removeFromList = (id: number) => setCards((items) => items.filter((c) => c.id !== id));

  const handleRestoreConfirm = (columnId?: number) => {
    const ids = restoreTarget;
    if (!ids?.length) return;

    setRestoring(true);
    restoreCards(ids, columnId)
      .then((res) => {
        if (!res || res.status >= 400) return;
        setCards((items) => items.filter((card) => !ids.includes(card.id)));
        setSelected((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
        setRestoreTarget(null);
      })
      .finally(() => setRestoring(false));
  };

  const handleDelete = (card: AiManagementCard) => {
    if (!window.confirm(`Excluir definitivamente a licitação "${card.bidding.notice_number}"?`))
      return;
    deleteCard(card.id).then((res) => {
      if (!res || res.status >= 400) return;
      removeFromList(card.id);
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-2">Não foi possível carregar as licitações arquivadas.</p>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={load}>
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <Container ref={printRef}>
      <h5 className="d-none d-print-block mb-3">Gestão IA - Licitações arquivadas</h5>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 d-print-none">
        <label className="mb-0 d-flex align-items-center" htmlFor="select-all-archived">
          <input
            id="select-all-archived"
            type="checkbox"
            className="mr-2"
            checked={allPageSelected}
            onChange={toggleAllPage}
            disabled={pageIds.length === 0}
          />
          Selecionar todas as licitações da página
        </label>

        <div className="d-flex">
          <div className="mr-2">
            <AiManagementCardMenu
              ariaLabel="Ações em massa"
              label="Ações"
              disabled={selected.size === 0}
              items={[
                {
                  label: 'Restaurar licitações',
                  onClick: () =>
                    setRestoreTarget(
                      cards.filter((card) => selected.has(card.id)).map((card) => card.id),
                    ),
                },
              ]}
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm mr-2"
            disabled={selected.size === 0}
          >
            Compartilhar
          </button>
          <AiManagementCardMenu
            ariaLabel="Exportar"
            label="Exportar"
            disabled={selected.size === 0}
            items={[
              { label: 'Gerar .xlsx', onClick: () => {} },
              { label: 'Gerar .docx', onClick: () => {} },
              { label: 'Imprimir', onClick: () => print?.() },
            ]}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table archived-table">
          <thead>
            <tr>
              <th style={{ width: 40 }} className="d-print-none" aria-label="Selecionar" />
              <th>Edital</th>
              <th>Órgão</th>
              <th>Objeto</th>
              <th>Prazo/Abertura</th>
              <th>Arquivado por</th>
              <th style={{ width: 40 }} className="d-print-none" aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {cards.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  Nenhuma licitação arquivada.
                </td>
              </tr>
            )}
            {visibleCards.map((card) => {
              const { bidding } = card;
              const dateRows = [
                { label: 'Prazo', iso: bidding.deadline_at },
                { label: 'Documento', iso: bidding.document_at },
                { label: 'Abertura', iso: bidding.opening_at },
              ].filter((row): row is { label: string; iso: string } => Boolean(row.iso));

              return (
                <tr key={card.id}>
                  <td className="d-print-none">
                    <input
                      type="checkbox"
                      checked={selected.has(card.id)}
                      onChange={() => toggleOne(card.id)}
                      aria-label={`Selecionar ${bidding.notice_number}`}
                    />
                  </td>
                  <td className="font-weight-bold">{bidding.notice_number}</td>
                  <td>
                    <div className="archived-orgao">
                      <div className="font-weight-bold text-dark text-truncate">
                        {bidding.public_body}
                      </div>
                      <div className="text-muted small text-truncate">
                        {bidding.city} - {bidding.state}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="archived-object">
                      <LongText fullText={bidding.object} />
                    </div>
                  </td>
                  <td>
                    {dateRows.map((row) => (
                      <div key={row.label} className="text-nowrap">
                        <span className="font-weight-bold">{row.label}:</span>{' '}
                        {formatDateTime(row.iso)}
                      </div>
                    ))}
                  </td>
                  <td className="text-nowrap">{archivedByLabel(card)}</td>
                  <td className="text-right d-print-none">
                    <AiManagementCardMenu
                      ariaLabel={`Opções da licitação ${bidding.notice_number}`}
                      items={[
                        { label: 'Ver licitação', onClick: () => setOpenCard(card) },
                        {
                          label: 'Restaurar licitação',
                          onClick: () => setRestoreTarget([card.id]),
                        },
                        {
                          label: 'Excluir licitação',
                          onClick: () => handleDelete(card),
                          danger: true,
                          dividerBefore: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-print-none">
          <Pagination
            postsPerPage={PAGE_SIZE}
            totalPosts={cards.length}
            currentPage={currentPage}
            paginate={setPage}
            firstAndLastPage
          />
        </div>
      )}

      {openCard && <AiManagementCardModal card={openCard} onClose={() => setOpenCard(null)} />}

      <AiManagementColumnPickerModal
        title={restoreTarget?.length === 1 ? 'Restaurar licitação' : 'Restaurar licitações'}
        columns={targetColumns}
        show={!!restoreTarget}
        saving={restoring}
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleRestoreConfirm}
      />
    </Container>
  );
};

export default AiManagementArchivedList;
